// useAudio.ts — Phase A audio hook. Drives three Howler-backed channels
// off engine state + a transient one-shot signal:
//
//   - ambient  (string|null, persistent)   continuous loop with 1s crossfade
//   - sporadic (string|null, persistent)   randomised-interval scheduler
//   - playSfx  (string|null, transient)    fire-and-forget single play
//
// The hook owns ALL Howler state internally — Howl instances are cached by
// tag so re-entering a previously-visited acoustic space doesn't reload
// audio; the cache is cleared on unmount.
//
// Mobile autoplay: browsers suspend the audio context until a user gesture.
// The caller (App.tsx) MUST call `Howler.ctx?.resume()` from the input
// submit handler on every turn (it's idempotent when already running). The
// first turn submission then doubles as the unlock gesture.

import { useEffect, useRef } from "react";
import { Howl, Howler } from "howler";
import {
  AMBIENT_CATALOGUE,
  SPORADIC_CATALOGUE,
  ONESHOT_CATALOGUE,
} from "./catalogue";

export interface UseAudioInput {
  /** Currently-playing ambient bed tag, or null for silent. */
  currentAmbient: string | null;
  /** Currently-scheduled sporadic punctuation tag, or null. */
  currentSporadic: string | null;
  /**
   * The latest one-shot signal. Hook fires the SFX once each time this
   * transitions to a new non-null value (null → "door_creak", or
   * "door_creak" → "glass_smash"). Repeats of the same value are
   * de-duplicated via a ref so re-renders don't double-fire.
   *
   * The natural caller pattern is to lift `result.stateDelta.playSfx`
   * into state after each turn — if the model emits the same tag on two
   * consecutive turns, increment a counter and pass `${tag}:${turn}` so
   * the value differs even when the tag repeats.
   */
  lastSfx: string | null;
  /** When true, all channels are silenced (but Howls stay loaded). */
  muted: boolean;
}

/** Path prefix for sound assets under `web/public/sounds/`. */
const SOUNDS_PREFIX = "/sounds/";

/** Crossfade duration between ambient beds, in ms. */
const AMBIENT_CROSSFADE_MS = 1000;

/** Default volumes (catalogue entries may override). */
const DEFAULT_AMBIENT_VOLUME = 1.0;
const DEFAULT_SPORADIC_VOLUME = 1.1;
const DEFAULT_ONESHOT_VOLUME = 1.0;

export function useAudio(input: UseAudioInput): void {
  const { currentAmbient, currentSporadic, lastSfx, muted } = input;

  // Cached Howl instances keyed by tag. Persist across renders so re-entry
  // to a previously-visited acoustic space doesn't reload audio.
  const ambientHowlsRef = useRef<Map<string, Howl>>(new Map());
  const sporadicHowlsRef = useRef<Map<string, Howl>>(new Map());
  const oneShotHowlsRef = useRef<Map<string, Howl>>(new Map());

  // Currently-playing ambient tag (separate from React state so the cleanup
  // path can read it). Sporadic equivalent stores the active timeout id.
  const activeAmbientRef = useRef<string | null>(null);
  const activeSporadicRef = useRef<string | null>(null);
  const sporadicTimeoutRef = useRef<number | null>(null);

  // De-dup token for one-shots. Compares strictly so the same signal value
  // never fires twice across re-renders.
  const lastFiredSfxRef = useRef<string | null>(null);

  // Global mute. Applied at the Howler level so already-playing audio
  // becomes inaudible without restarting on unmute.
  useEffect(() => {
    Howler.mute(muted);
  }, [muted]);

  // ── Ambient channel ──────────────────────────────────────────────────
  useEffect(() => {
    const ambientHowls = ambientHowlsRef.current;
    const previousTag = activeAmbientRef.current;
    const nextTag = currentAmbient;

    if (previousTag === nextTag) return;

    // Crossfade out the previous bed.
    if (previousTag) {
      const prev = ambientHowls.get(previousTag);
      if (prev) {
        const fromVol = prev.volume();
        prev.fade(fromVol, 0, AMBIENT_CROSSFADE_MS);
        // Stop after the fade completes so the loop doesn't keep ticking
        // muted in the background.
        window.setTimeout(() => {
          // Only stop if it hasn't been re-started in the meantime.
          if (activeAmbientRef.current !== previousTag) {
            prev.stop();
            prev.volume(fromVol); // restore for next time
          }
        }, AMBIENT_CROSSFADE_MS + 50);
      }
    }

    // Fade in the new bed.
    if (nextTag) {
      const entry = AMBIENT_CATALOGUE[nextTag];
      if (!entry) {
        console.warn("[audio] unknown ambient tag:", nextTag);
        activeAmbientRef.current = null;
        return;
      }
      const targetVolume = entry.baseVolume ?? DEFAULT_AMBIENT_VOLUME;
      let howl = ambientHowls.get(nextTag);
      if (!howl) {
        howl = new Howl({
          src: [SOUNDS_PREFIX + entry.src],
          loop: true,
          html5: false,
          volume: 0,
        });
        ambientHowls.set(nextTag, howl);
      }
      howl.volume(0);
      howl.play();
      howl.fade(0, targetVolume, AMBIENT_CROSSFADE_MS);
    }

    activeAmbientRef.current = nextTag;
  }, [currentAmbient]);

  // ── Sporadic channel ─────────────────────────────────────────────────
  useEffect(() => {
    const sporadicHowls = sporadicHowlsRef.current;
    const previousTag = activeSporadicRef.current;
    const nextTag = currentSporadic;

    if (previousTag === nextTag) return;

    // Cancel any pending fire of the previous tag.
    if (sporadicTimeoutRef.current !== null) {
      window.clearTimeout(sporadicTimeoutRef.current);
      sporadicTimeoutRef.current = null;
    }

    activeSporadicRef.current = nextTag;
    if (!nextTag) return;

    const entry = SPORADIC_CATALOGUE[nextTag];
    if (!entry) {
      console.warn("[audio] unknown sporadic tag:", nextTag);
      activeSporadicRef.current = null;
      return;
    }

    let howl = sporadicHowls.get(nextTag);
    if (!howl) {
      howl = new Howl({
        src: [SOUNDS_PREFIX + entry.src],
        loop: false,
        html5: false,
        volume: entry.baseVolume ?? DEFAULT_SPORADIC_VOLUME,
      });
      sporadicHowls.set(nextTag, howl);
    }
    const playable = howl;

    // Schedule next play. Reschedules itself in the play callback.
    const scheduleNext = () => {
      // Bail if the active tag changed mid-flight (cleanup path).
      if (activeSporadicRef.current !== nextTag) return;
      const min = entry.minIntervalMs;
      const max = entry.maxIntervalMs;
      const delay =
        min === max ? min : min + Math.random() * Math.max(0, max - min);
      sporadicTimeoutRef.current = window.setTimeout(() => {
        // Re-check active tag at fire time too — the player could have
        // moved acoustic space during the delay.
        if (activeSporadicRef.current !== nextTag) return;
        playable.play();
        scheduleNext();
      }, delay);
    };
    scheduleNext();
  }, [currentSporadic]);

  // ── One-shot channel ─────────────────────────────────────────────────
  useEffect(() => {
    if (!lastSfx) return;
    if (lastSfx === lastFiredSfxRef.current) return;
    lastFiredSfxRef.current = lastSfx;

    // Allow the caller to pass either a bare tag or `${tag}:${nonce}` so
    // identical repeated tags can still fire (see UseAudioInput.lastSfx).
    const colonIdx = lastSfx.indexOf(":");
    const tag = colonIdx >= 0 ? lastSfx.slice(0, colonIdx) : lastSfx;
    if (!tag) return;

    const entry = ONESHOT_CATALOGUE[tag];
    if (!entry) {
      console.warn("[audio] unknown one-shot tag:", tag);
      return;
    }

    let howl = oneShotHowlsRef.current.get(tag);
    if (!howl) {
      howl = new Howl({
        src: [SOUNDS_PREFIX + entry.src],
        loop: false,
        html5: false,
        volume: entry.baseVolume ?? DEFAULT_ONESHOT_VOLUME,
      });
      oneShotHowlsRef.current.set(tag, howl);
    }
    howl.play();
  }, [lastSfx]);

  // ── Cleanup on unmount ───────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (sporadicTimeoutRef.current !== null) {
        window.clearTimeout(sporadicTimeoutRef.current);
        sporadicTimeoutRef.current = null;
      }
      activeAmbientRef.current = null;
      activeSporadicRef.current = null;
      for (const h of ambientHowlsRef.current.values()) h.unload();
      for (const h of sporadicHowlsRef.current.values()) h.unload();
      for (const h of oneShotHowlsRef.current.values()) h.unload();
      ambientHowlsRef.current.clear();
      sporadicHowlsRef.current.clear();
      oneShotHowlsRef.current.clear();
    };
  }, []);
}
